'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, IdCard, Lock, RotateCcw, CheckCircle } from 'lucide-react';
import Image from 'next/image';

interface LoginFormData {
  bhishmId: string;
  password: string;
}

const DEMO_CREDENTIALS = [
  { role: 'Store Keeper', bhishmId: 'BHISHM/HLL/TECH/PTN/01', password: 'HLL@AiimsPtn2026' },
  { role: 'Supply Officer', bhishmId: 'BHISHM/HLL/SO/DEL/01', password: 'HLL@SfjDel2026' },
  { role: 'Admin', bhishmId: 'BHISHM/HLL/ADM/HQ/01', password: 'HLL@AdminHQ2026' },
];

function validateBhishmId(id: string): boolean {
  // Format: BHISHM/XXX/XXX/XXX/XX
  const pattern = /^BHISHM\/[A-Z0-9]+\/[A-Z0-9]+\/[A-Z0-9]+\/\d{2}$/i;
  return pattern.test(id.trim());
}

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bhishmId, setBhishmId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetId, setResetId] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!bhishmId.trim()) {
      setError('Please enter your BHISHM ID.');
      return;
    }
    if (!validateBhishmId(bhishmId)) {
      setError('Invalid BHISHM ID format. Expected: BHISHM/HLL/TECH/DEL/01');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));

    const valid = DEMO_CREDENTIALS.some(
      (c) => c.bhishmId.toLowerCase() === bhishmId.trim().toLowerCase() && c.password === password
    );

    if (!valid) {
      setError('Invalid BHISHM ID or password. Use a demo account below to sign in.');
      setIsLoading(false);
      return;
    }

    setTimeout(() => router.push('/'), 600);
    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetId.trim()) {
      setError('Please enter your BHISHM ID to reset password.');
      return;
    }
    if (!validateBhishmId(resetId)) {
      setError('Invalid BHISHM ID format. Expected: BHISHM/HLL/TECH/DEL/01');
      return;
    }
    setResetLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setResetLoading(false);
    setResetSent(true);
  };

  const inputStyle = {
    background: '#f8fafc',
    border: '1.5px solid #cbd5e1',
    color: '#1a2332',
  };

  const inputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = '#0369a1';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(3,105,161,0.12)';
    e.currentTarget.style.background = '#ffffff';
  };

  const inputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = '#cbd5e1';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.background = '#f8fafc';
  };

  if (showReset) {
    return (
      <div
        className="w-full rounded-2xl p-8 space-y-6"
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 60px rgba(3,105,161,0.1), 0 4px 16px rgba(0,0,0,0.06)',
        }}
      >
        <div className="space-y-1">
          <button
            onClick={() => { setShowReset(false); setResetSent(false); setResetId(''); setError(''); }}
            className="flex items-center gap-1.5 text-xs font-500 mb-4 transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#0369a1'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#64748b'; }}
          >
            ← Back to Sign In
          </button>
          <h1 className="text-xl font-700 leading-snug" style={{ color: '#0f2744' }}>
            Reset Password
          </h1>
          <p className="text-sm" style={{ color: '#64748b' }}>
            Enter your BHISHM ID to receive reset instructions
          </p>
        </div>

        {resetSent ? (
          <div
            className="flex flex-col items-center gap-3 py-6 text-center rounded-xl"
            style={{ background: '#f0fdf4', border: '1px solid rgba(22,163,74,0.25)' }}
          >
            <CheckCircle size={40} style={{ color: '#16a34a' }} />
            <div>
              <p className="text-sm font-600" style={{ color: '#15803d' }}>Reset Instructions Sent</p>
              <p className="text-xs mt-1" style={{ color: '#16a34a' }}>
                Contact your system administrator with your BHISHM ID: <strong>{resetId}</strong>
              </p>
            </div>
            <button
              onClick={() => { setShowReset(false); setResetSent(false); setResetId(''); setError(''); }}
              className="mt-2 text-xs font-600 px-4 py-2 rounded-lg transition-all"
              style={{ background: '#0369a1', color: 'white' }}
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <label className="block text-xs font-600 uppercase tracking-wider" style={{ color: '#64748b' }}>
                BHISHM ID
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }}>
                  <IdCard size={15} />
                </span>
                <input
                  type="text"
                  value={resetId}
                  onChange={(e) => { setResetId(e.target.value.toUpperCase()); setError(''); }}
                  placeholder="BHISHM/HLL/TECH/DEL/01"
                  className="w-full pl-9 pr-4 py-3 text-sm rounded-lg outline-none transition-all font-mono"
                  style={inputStyle}
                  onFocus={inputFocus}
                  onBlur={inputBlur}
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-lg px-3 py-2.5" style={{ background: '#fef2f2', border: '1px solid rgba(220,38,38,0.25)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" className="mt-0.5 flex-shrink-0">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p className="text-xs" style={{ color: '#dc2626' }}>{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={resetLoading}
              className="w-full py-3 text-sm font-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed btn-scale"
              style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)', boxShadow: '0 4px 14px rgba(3,105,161,0.3)' }}
            >
              {resetLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={15} className="animate-spin" />
                  Sending...
                </span>
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div
      className="w-full rounded-2xl p-8 space-y-6"
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 20px 60px rgba(3,105,161,0.1), 0 4px 16px rgba(0,0,0,0.06)',
      }}
    >
      {/* Heading */}
      <div className="space-y-1">
        {/* HLL Logo */}
        <div className="flex justify-center mb-6">
          <Image
            src="/assets/images/hll-CHrxdcw--1782330934250.png"
            alt="HLL Lifecare Limited logo"
            width={180}
            height={66}
            className="object-contain"
            style={{ maxHeight: '66px', width: 'auto' }}
          />
        </div>
        <h1 className="text-xl font-700 leading-snug" style={{ color: '#0f2744' }}>
          Welcome back
        </h1>
        <p className="text-sm" style={{ color: '#64748b' }}>Sign in with your BHISHM ID</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* BHISHM ID */}
        <div className="space-y-1.5">
          <label className="block text-xs font-600 uppercase tracking-wider" style={{ color: '#64748b' }}>
            BHISHM ID
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }}>
              <IdCard size={15} />
            </span>
            <input
              type="text"
              value={bhishmId}
              onChange={(e) => { setBhishmId(e.target.value.toUpperCase()); setError(''); }}
              placeholder="BHISHM/HLL/TECH/DEL/01"
              className="w-full pl-9 pr-4 py-3 text-sm rounded-lg outline-none transition-all font-mono tracking-wide"
              style={inputStyle}
              onFocus={inputFocus}
              onBlur={inputBlur}
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-600 uppercase tracking-wider" style={{ color: '#64748b' }}>
              Password
            </label>
            <button
              type="button"
              onClick={() => { setShowReset(true); setError(''); }}
              className="text-[11px] font-500 transition-colors flex items-center gap-1"
              style={{ color: '#0369a1' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#0284c7'; (e.currentTarget as HTMLButtonElement).style.textDecoration = 'underline'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#0369a1'; (e.currentTarget as HTMLButtonElement).style.textDecoration = 'none'; }}
            >
              <RotateCcw size={10} />
              Reset Password
            </button>
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }}>
              <Lock size={15} />
            </span>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Enter your password"
              className="w-full pl-9 pr-10 py-3 text-sm rounded-lg outline-none transition-all"
              style={inputStyle}
              onFocus={inputFocus}
              onBlur={inputBlur}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: '#94a3b8' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#475569'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; }}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 rounded-lg px-3 py-2.5" style={{ background: '#fef2f2', border: '1px solid rgba(220,38,38,0.25)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" className="mt-0.5 flex-shrink-0">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="text-xs" style={{ color: '#dc2626' }}>{error}</p>
          </div>
        )}

        {/* Sign In button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 text-sm font-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed btn-scale"
          style={{
            background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
            boxShadow: '0 4px 14px rgba(3,105,161,0.3)',
          }}
          onMouseEnter={e => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(3,105,161,0.4)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 14px rgba(3,105,161,0.3)'; }}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={15} className="animate-spin" />
              Signing in...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* Demo Accounts */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
          <span className="text-[11px] font-600 uppercase tracking-wider" style={{ color: '#94a3b8' }}>Demo Accounts</span>
          <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {DEMO_CREDENTIALS.map((cred) => (
            <button
              key={cred.role}
              type="button"
              onClick={() => {
                setBhishmId(cred.bhishmId);
                setPassword(cred.password);
                setError('');
              }}
              className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-center transition-all"
              style={{
                background: '#f0f9ff',
                border: '1.5px solid #bae6fd',
                color: '#0369a1',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = '#e0f2fe';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#0369a1';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = '#f0f9ff';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#bae6fd';
              }}
            >
              <span className="text-[11px] font-700 leading-tight" style={{ color: '#0369a1' }}>{cred.role}</span>
              <span className="text-[9px] font-500 leading-tight" style={{ color: '#64748b' }}>Click to fill</span>
            </button>
          ))}
        </div>
        <p className="text-center text-[10px]" style={{ color: '#94a3b8' }}>
          Select a demo role to auto-fill credentials, then click Sign In
        </p>
      </div>

    </div>
  );
}